import axios, { type AxiosRequestConfig } from 'axios';
import { buildHttpGetDedupeKey, runDeduped, shouldDedupeAxiosGet } from '../utils/inflightDedupe';
import { uiHealthOnAuthFallback, uiHealthOnError, uiHealthOnResponse } from './uiHealth';
import {
  createAccessTokenRefreshScheduler,
  redirectToLoginAfterRefreshFailure,
  type AccessTokenRefreshScheduler,
} from './accessTokenRefresh';
import {
  clearAuthTokens,
  getRefreshCsrfToken,
  getStoredAccessToken,
  getStoredRefreshToken,
  hasBrowserRefreshSession,
  storeAccessTokenOnly,
  storeAuthTokens,
} from './authStorage';

// Default to same-origin so the dashboard can be fronted by nginx and proxy /api/* to cloud_api.
// This avoids hardcoding localhost (which breaks when you open the dashboard from another machine).
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000');
const AUTH_SESSION_HEADER =
  process.env.REACT_APP_AUTH_SESSION_HEADER || 'X-Edgentik-Session';
const AUTH_SESSION_MODE =
  process.env.REACT_APP_AUTH_SESSION_MODE || 'browser-cookie';

type UiScopeSource = 'localStorage' | 'jwt' | 'none';

function _safeTrim(v: unknown): string {
  return String(v ?? '').trim();
}

function _readLocalStorage(key: string): string {
  try {
    return _safeTrim(localStorage.getItem(key));
  } catch {
    return '';
  }
}

function _writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore (private mode / storage disabled)
  }
}

function _removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function _decodeJwtPayload(token: string): Record<string, any> | null {
  const t = _safeTrim(token);
  if (!t) return null;
  const parts = t.split('.');
  if (parts.length < 2) return null;
  const payload = parts[1] || '';
  if (!payload) return null;
  try {
    const pad = '='.repeat((4 - (payload.length % 4)) % 4);
    const b64 = (payload + pad).replace(/-/g, '+').replace(/_/g, '/');
    const json = typeof window !== 'undefined' && typeof window.atob === 'function' ? window.atob(b64) : '';
    if (!json) return null;
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Read the UI org/site scope.
 *
 * Order of precedence:
 * - explicit localStorage scope (ui.scope.*)
 * - JWT claims (org_id/site_id) when present (single-scope users)
 *
 * Note: this function may persist JWT-derived scope into localStorage (best-effort)
 * so that site-scoped UI routes (e.g. `/api/v1/sites/<site_id>/...`) can use it.
 */
export function ensureUiScope(): { orgId: string; siteId: string; source: UiScopeSource } {
  const orgLs = _readLocalStorage('ui.scope.org_id');
  const siteLs = _readLocalStorage('ui.scope.site_id');
  const userSet = _readLocalStorage('ui.scope.user_set') === 'true';
  const isDefaultPair = orgLs === 'default' && siteLs === 'default';
  // Only treat default/default as a "real" scope if the user explicitly set it.
  // Otherwise it acts like a dangerous silent fallback that hides data in other scopes.
  if (orgLs && siteLs && (!isDefaultPair || userSet)) {
    return { orgId: orgLs, siteId: siteLs, source: 'localStorage' };
  }

  const token = getStoredAccessToken();
  const payload = token ? _decodeJwtPayload(token) : null;
  const orgJwt = _safeTrim(payload?.org_id);
  const siteJwt = _safeTrim(payload?.site_id);
  const orgPref = _safeTrim(payload?.preferred_org_id);
  const sitePref = _safeTrim(payload?.preferred_site_id);
  if (orgJwt && siteJwt) {
    // Persist for parts of the UI that build site-scoped URLs outside axios interceptors.
    if (!orgLs) _writeLocalStorage('ui.scope.org_id', orgJwt);
    if (!siteLs) _writeLocalStorage('ui.scope.site_id', siteJwt);
    return { orgId: orgJwt, siteId: siteJwt, source: 'jwt' };
  }
  if (orgPref && sitePref) {
    // Multi-scope bootstrap: prefer server-selected non-default scope when available.
    if (!orgLs || isDefaultPair) _writeLocalStorage('ui.scope.org_id', orgPref);
    if (!siteLs || isDefaultPair) _writeLocalStorage('ui.scope.site_id', sitePref);
    return { orgId: orgPref, siteId: sitePref, source: 'jwt' };
  }

  // Mixed claims (e.g. org_id + preferred_site_id): align with cloud_api JWT scope merge
  // (see websocket manager / tenant_scope JWT resolution) so GETs like /api/v1/devices/ get org_id & site_id query params.
  const orgMerged = orgJwt || orgPref;
  const siteMerged = siteJwt || sitePref;
  if (orgMerged && siteMerged) {
    if (!orgLs || isDefaultPair) _writeLocalStorage('ui.scope.org_id', orgMerged);
    if (!siteLs || isDefaultPair) _writeLocalStorage('ui.scope.site_id', siteMerged);
    return { orgId: orgMerged, siteId: siteMerged, source: 'jwt' };
  }

  // If the only "scope" we have is an implicit default/default, treat it as unset.
  if (orgLs && siteLs && isDefaultPair && !userSet) {
    return { orgId: '', siteId: '', source: 'none' };
  }
  return { orgId: '', siteId: '', source: 'none' };
}

export function persistUiScope(orgIdRaw?: unknown, siteIdRaw?: unknown, opts?: { userSet?: boolean }): boolean {
  const orgId = _safeTrim(orgIdRaw);
  const siteId = _safeTrim(siteIdRaw);
  if (!orgId || !siteId) return false;
  _writeLocalStorage('ui.scope.org_id', orgId);
  _writeLocalStorage('ui.scope.site_id', siteId);
  if (opts?.userSet === true) _writeLocalStorage('ui.scope.user_set', 'true');
  else _removeLocalStorage('ui.scope.user_set');
  return true;
}

/**
 * Resolve org/site for API requests with URL override support.
 * Backend tenant scope in prod requires org_id/site_id as query/body params (headers are dev-only).
 * Use this to inject scope into all requests so pages don't need per-call params.
 */
function _scopeForRequest(): { orgId: string; siteId: string } | null {
  const scope = ensureUiScope();
  try {
    const sp = new URLSearchParams(typeof window !== 'undefined' ? (window.location?.search || '') : '');
    const qSite = (sp.get('site') || sp.get('site_id') || '').trim();
    const qOrg = (sp.get('org') || sp.get('org_id') || '').trim();
    const orgId = qOrg || scope.orgId;
    const siteId = qSite || scope.siteId;
    if (orgId && siteId) {
      return { orgId, siteId };
    }
  } catch {
    // ignore and fall back to stored scope
  }
  if (scope.orgId && scope.siteId) return { orgId: scope.orgId, siteId: scope.siteId };
  return null;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000,  // F-06: 120 seconds default timeout (for Preflight live view which can take 60-90s)
});

// Request interceptor to add auth token and tenant scope
api.interceptors.request.use(
  (config) => {
    // Full RBAC: supply org/site scope. Backend tenant scope in prod requires org_id/site_id
    // as query/body params (X-Org-Id/X-Site-Id headers are dev-only). Inject params so all
    // pages get scope without per-call boilerplate.
    try {
      const reqScope = _scopeForRequest();
      if (reqScope?.orgId && !config.headers['X-Org-Id']) config.headers['X-Org-Id'] = reqScope.orgId;
      if (reqScope?.siteId && !config.headers['X-Site-Id']) config.headers['X-Site-Id'] = reqScope.siteId;
      if (reqScope?.orgId && reqScope?.siteId) {
        const existing = config.params && typeof config.params === 'object' && !(config.params instanceof URLSearchParams)
          ? config.params : {};
        const params = { ...existing };
        if (!params.org_id) params.org_id = reqScope.orgId;
        if (!params.site_id) params.site_id = reqScope.siteId;
        config.params = params;
      }
    } catch {
      // ignore (private mode / storage disabled)
    }

    const token = getStoredAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (!config.headers['X-Actor'] && !config.headers['X-User-Email']) {
        const payload = _decodeJwtPayload(token);
        const actorLabel = _safeTrim(payload?.email)
          || _safeTrim(payload?.preferred_username)
          || _safeTrim(payload?.username)
          || _safeTrim(payload?.sub);
        if (actorLabel) {
          config.headers['X-User-Email'] = actorLabel;
        }
      }
    } else {
      // Fallback: use X-API-Key if available (for control plane endpoints)
      // This allows camera management to work without user login
      const apiKey = process.env.REACT_APP_CONTROL_PLANE_API_KEY;
      if (apiKey) {
        config.headers['X-API-Key'] = apiKey;
        // IMPORTANT: do NOT silently force org/site to default/default.
        // If scope is missing, leave it unset so site-scoped pages can prompt the operator to set scope explicitly.
      }
    }
    return config;
  },
  async (error) => {
    return Promise.reject(error);
  }
);

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getStoredRefreshToken();
  const csrfToken = getRefreshCsrfToken();
  const usingBrowserRefreshSession = Boolean(csrfToken);
  if (!refreshToken && !usingBrowserRefreshSession) return null;
  const headers: Record<string, string> = {};
  if (refreshToken) {
    headers.Authorization = `Bearer ${refreshToken}`;
  }
  if (usingBrowserRefreshSession) {
    headers[AUTH_SESSION_HEADER] = AUTH_SESSION_MODE;
    headers['X-CSRF-TOKEN'] = csrfToken;
  }
  refreshPromise = axios
    .post(
      `${API_BASE_URL}/api/v1/auth/refresh`,
      {},
      {
        headers,
        timeout: 10000,
        withCredentials: usingBrowserRefreshSession,
      }
    )
    .then((response) => {
      const accessToken = String(response.data?.access_token || '').trim();
      const nextRefreshToken = String(response.data?.refresh_token || '').trim();
      if (!accessToken) {
        clearAuthTokens();
        return null;
      }
      if (usingBrowserRefreshSession && !nextRefreshToken) {
        storeAccessTokenOnly(accessToken);
      } else if (nextRefreshToken) {
        storeAuthTokens(accessToken, nextRefreshToken);
      } else {
        clearAuthTokens();
        return null;
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      armStoredAccessTokenRefresh();
      return accessToken;
    })
    .catch(() => {
      clearAuthTokens();
      delete api.defaults.headers.common['Authorization'];
      clearScheduledAccessTokenRefresh();
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => {
    try {
      uiHealthOnResponse(response?.config?.url, response?.config?.method);
    } catch {
      // ignore
    }
    return response;
  },
  async (error) => {
    // 401/403 brick elimination:
    // When JWT is expired or missing, server may return 401. When JWT is invalid/forbidden, 403.
    // If a control-plane API key is available, retry once with X-API-Key instead of failing.
    const status = error.response?.status;
    const originalConfig: any = error.config;
    const hasTriedFallback = Boolean(originalConfig?.__edgentikAuthFallbackTried);
    const token = getStoredAccessToken();
    const refreshToken = getStoredRefreshToken();
    const hasBrowserRefresh = hasBrowserRefreshSession();
    const apiKey = process.env.REACT_APP_CONTROL_PLANE_API_KEY;
    let refreshFailed = false;

    if ((status === 401 || status === 422) && token && (refreshToken || hasBrowserRefresh) && originalConfig && !originalConfig.__edgentikRefreshTried) {
      originalConfig.__edgentikRefreshTried = true;
      const nextAccessToken = await refreshAccessToken();
      if (nextAccessToken) {
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${nextAccessToken}`;
        return api(originalConfig);
      }
      refreshFailed = true;
    }

    const method = String(originalConfig?.method || '').trim().toUpperCase();
    const canFallbackToApiKey =
      !method || method === 'GET' || method === 'HEAD' || method === 'OPTIONS';

    if ((status === 401 || status === 403) && apiKey && originalConfig && !hasTriedFallback && canFallbackToApiKey) {
      try {
        if (token) {
          clearAuthTokens();
          delete api.defaults.headers.common['Authorization'];
        }
        originalConfig.__edgentikAuthFallbackTried = true;
        originalConfig.headers = originalConfig.headers || {};
        delete originalConfig.headers.Authorization;
        delete originalConfig.headers.authorization;
        originalConfig.headers['X-API-Key'] = apiKey;
        try {
          window.dispatchEvent(
            new CustomEvent('edgentik:auth-fallback', {
              detail: { mode: 'api_key', reason: status === 401 ? 'unauthorized' : 'forbidden' },
            })
          );
        } catch {
          // ignore
        }
        try {
          uiHealthOnAuthFallback();
        } catch {
          // ignore
        }
        return api(originalConfig);
      } catch {
        // Fall through to 401 redirect logic if retry fails
      }
    }

    if (refreshFailed) {
      redirectToLoginAfterRefreshFailure();
    }

    if (status === 401) {
      const hasTokenNow = Boolean(getStoredAccessToken());
      const onLoginRoute = typeof window !== 'undefined' && window.location?.pathname === '/login';

      if (hasTokenNow) {
        clearAuthTokens();
        delete api.defaults.headers.common['Authorization'];
      }

      if (hasTokenNow && !onLoginRoute) {
        redirectToLoginAfterRefreshFailure();
      }
    }
    try {
      uiHealthOnError(error?.config?.url, error?.config?.method, error?.response?.status, error?.message);
    } catch {
      // ignore
    }
    return Promise.reject(error);
  }
);

let accessTokenRefreshScheduler: AccessTokenRefreshScheduler | null = null;

function accessTokenRefreshSchedulerInstance(): AccessTokenRefreshScheduler {
  if (!accessTokenRefreshScheduler) {
    accessTokenRefreshScheduler = createAccessTokenRefreshScheduler({
      getExp: (accessToken) => {
        const payload = _decodeJwtPayload(accessToken);
        const exp = Number(payload?.exp);
        return Number.isFinite(exp) ? exp : null;
      },
      canRefresh: () => Boolean(getStoredRefreshToken() || hasBrowserRefreshSession()),
      refresh: () => refreshAccessToken(),
      onFailed: () => {
        redirectToLoginAfterRefreshFailure();
      },
    });
  }
  return accessTokenRefreshScheduler;
}

export function armStoredAccessTokenRefresh(): void {
  const token = getStoredAccessToken();
  const scheduler = accessTokenRefreshSchedulerInstance();
  if (!token) {
    scheduler.clear();
    return;
  }
  scheduler.arm(token);
}

export function clearScheduledAccessTokenRefresh(): void {
  accessTokenRefreshSchedulerInstance().clear();
}

// Phase 1.2: coalesce concurrent identical GETs (same url + params as passed by caller).
const _axiosGet = api.get.bind(api);
api.get = ((url: string, config?: AxiosRequestConfig) => {
  if (!shouldDedupeAxiosGet(config ?? null)) {
    return _axiosGet(url, config);
  }
  const key = buildHttpGetDedupeKey(url, config ?? null);
  return runDeduped(key, () => _axiosGet(url, config));
}) as typeof api.get;

// API endpoints
export const endpoints = {
  auth: {
    login: '/api/v1/auth/login',
    register: '/api/v1/auth/register',
    logout: '/api/v1/auth/logout',
  },
  configurations: {
    list: '/api/v1/configs/',
    create: '/api/v1/configs/',
    get: (id: string) => `/api/v1/configs/${id}`,
    update: (id: string) => `/api/v1/configs/${id}`,
    delete: (id: string) => `/api/v1/configs/${id}`,
  },
  devices: {
    list: '/api/v1/devices/',
    create: '/api/v1/devices/',
    ensure: '/api/v1/devices/ensure',
    get: (id: string) => `/api/v1/devices/${id}`,
    update: (id: string) => `/api/v1/devices/${id}`,
    delete: (id: string) => `/api/v1/devices/${id}`,
    groups: '/api/v1/devices/groups',
  },
  enterpriseDeploy: {
    start: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/deploy/enterprise`,
    latest: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/deploy/enterprise/latest`,
    acceptance: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/deploy/enterprise/acceptance`,
  },
  commission: {
    bootstrapIdentity: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/bootstrap_identity`,
    bootstrapTrustProbe: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/bootstrap_trust_probe`,
    fromZero: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/commission_from_zero`,
    oneClick: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/commission_one_click`,
    status: (deviceId: string, jobId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/commission_one_click/${encodeURIComponent(jobId)}`,
    latest: (deviceId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/commission_one_click/latest`,
    cancel: (deviceId: string, jobId: string) =>
      `/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/commission_one_click/${encodeURIComponent(jobId)}/cancel`,
  },
  deployments: {
    list: '/api/v1/deployments/',
    create: '/api/v1/deployments/',
    get: (id: string) => `/api/v1/deployments/${id}`,
    start: (id: string) => `/api/v1/deployments/${id}/start`,
    cancel: (id: string) => `/api/v1/deployments/${id}/cancel`,
    clarity: {
      list: '/api/v1/deployments/clarity/deployments',
      get: (deploymentId: string) => `/api/v1/deployments/clarity/deployments/${deploymentId}`,
    },
  },
  health: '/health',
};
